import { useToggle } from '@mews/framework';
import { Badge, Container, Grid, Stack, Typography } from '@optimus-web/core';
import { TagList } from '../../tagList/TagList';
import { SubSectionLine as SubSectionLineType } from '../types';
import { ExpandableLineContent } from './ExpandableLineContent';
import { ExpandableLineTriggerText } from './ExpandableLineTriggerText';
import { LineIcon } from './LineIcon';
import { isSubSectionLineObject } from './utils';

interface SubSectionLineProps {
    line: SubSectionLineType;
    dataTestId: string;
}

export const SubSectionLine: React.FC<SubSectionLineProps> = ({ line, dataTestId }: SubSectionLineProps) => {
    const isLineObject = isSubSectionLineObject(line);
    const lineName = isLineObject ? line.name : line;
    const hasTwoColumns = isLineObject && line.items?.length;
    const templateColumns = hasTwoColumns ? 'auto minmax(0, 1fr)' : 'minmax(0, 1fr)';
    const maxColumnWidth = hasTwoColumns ? '195px' : 'auto';
    const [expanded, toggle] = useToggle();
    const hasExpandableContent = isLineObject && line.expandableContent;

    return (
        <>
            <Grid data-test-id={dataTestId} templateColumns={templateColumns} gap="100" alignItems="start">
                <Stack spacing="100" itemAlignment="center">
                    {isLineObject && line.icon && <LineIcon icon={line.icon} />}
                    <Container
                        maxWidth={maxColumnWidth}
                        padding={['0', '0', '0', !isLineObject || !line.icon ? '500' : '0']}
                    >
                        <Typography
                            ellipsis
                            textStyle={isLineObject && line.strong ? 'bodyMediumStrong' : 'bodyMedium'}
                            as="div"
                        >
                            {lineName}
                        </Typography>
                    </Container>
                    {isLineObject && line.badge && <Badge variant={line.badgeVariant} content={line.badge} />}
                    {hasExpandableContent && <ExpandableLineTriggerText expanded={expanded} onClick={toggle} />}
                </Stack>
                {isLineObject && line.items?.length && (
                    <Stack fill itemAlignment="center">
                        <TagList
                            compact
                            tagsVariant="basicSubtle"
                            tags={line.items.map((v) => ({ value: v.name, leadingIcon: v.icon }))}
                        />
                    </Stack>
                )}
            </Grid>
            {hasExpandableContent && (
                <ExpandableLineContent expanded={expanded}>{line.expandableContent}</ExpandableLineContent>
            )}
        </>
    );
};
